/**
 * APP Plus year logic — 2026 (D13). The founding year module: binds the 2026
 * implementations (engine in @/lib/app-plus-scoring, resolution in
 * AppPlusDataService) and OWNS the pure input/decode/scenario functions that
 * previously lived in the useAppPlusScoring hook.
 *
 * 2027 will be `export * from '../2026'` in its own module, overriding only
 * what CMS changed. The 2026 test fixtures are FROZEN — never edit them for a
 * later year's rules.
 */
import {
  scoreClinicalMeasureForAco, scoreCahps, scoreAdminClaims, assembleAppQualityScore,
  type ClinicalMeasureEntry, type MeasureScoreResult, type AcoScoringConfig,
  type AppQualityScore, type CahpsSsmEntry, type AdminClaimsEntry, type CahpsScoreResult,
} from '@/lib/app-plus-scoring';
import {
  AppPlusDataService, type AppCollectionType, type AppProgramConstants,
  type AdminClaimsScoringConfig, type AppBenchmarkCell, type CahpsSsm,
} from '@/lib/app-plus-data-service';
import type {
  AppPlusYearLogic, AppMeasureSet, StoredSubmission, CalculatedCountsByType,
  EngineInputs, SimulateScenario, LaneMemory,
} from '../types';
import { computeMipsExposure } from './exposure';

export { computeMipsExposure, adjustmentPctForScore } from './exposure';

const svc = () => AppPlusDataService.getInstance();

export const CLINICAL_IDS = ['001', '236', '134', '112', '113'] as const;

/** Rows → engine inputs. Malformed payloads decode to empty (fail soft, never throw). */
export function decodeSubmissions(rows: StoredSubmission[], _year: number): EngineInputs {
  const byId = new Map(rows.map(r => [r.measure_id, r]));

  const clinical: ClinicalMeasureEntry[] = CLINICAL_IDS.map(id => {
    const r = byId.get(id);
    return {
      measureId: id,
      collectionType: (r?.collection_type as ClinicalMeasureEntry['collectionType']) ?? 'MIPS CQM',
      reported: r?.reported ?? false,          // missing row = unreported (§3)
      eligiblePopulation: r?.eligible_population ?? 0,
      performanceMet: r?.performance_met ?? 0,
      performanceNotMet: r?.performance_not_met ?? 0,
      denominatorExceptions: r?.denominator_exceptions ?? 0,
      denominatorExclusions: r?.denominator_exclusions ?? 0,
    };
  });

  const cahpsRow = byId.get('321');
  const rawSsm = cahpsRow?.payload?.ssmEntries;
  const cahpsEntries = Array.isArray(rawSsm)
    ? rawSsm.filter(e => e && typeof e.ssmId === 'string' && Number.isFinite(e.n) && Number.isFinite(e.ratePct))
    : [];

  const adminEntries: AdminClaimsEntry[] = (['479', '484'] as const).flatMap(id => {
    const e = byId.get(id)?.payload?.adminEntry;
    return e && e.measureId === id ? [e] : [];
  });

  return { clinical, cahpsEntries, cahpsAdministeredInData: !!cahpsRow?.reported, adminEntries };
}

/** aco_config DB row → engine config. */
export function configFromRow(row: Record<string, unknown> | null): AcoScoringConfig {
  const r = row ?? {};
  return {
    isSmallPractice: !!r.is_small_practice,
    track: (r.track as AcoScoringConfig['track']) ?? 'ENHANCED',
    isFirstYearFirstAgreement: !!r.is_first_year_first_agreement,
    cahpsAdministered: r.cahps_administered !== false,
    cahpsSamplingExceptionApproved: !!r.cahps_sampling_exception,
    eucApproved: !!r.euc_approved,
    savingsPoolDollars: r.savings_pool_dollars != null ? Number(r.savings_pool_dollars) : null,
    priorYearAchievementPct: r.prior_year_achievement_pct != null ? Number(r.prior_year_achievement_pct) : null,
  };
}

/**
 * Merge calc-mode rows with their pipeline counts (D12 + R14 Phase E). A
 * clinical row with payload.mode === 'calc' ALWAYS reflects the pipeline —
 * counts from the lane matching the row's selected collection-type pill (a
 * Medicare CQM upload must never populate MIPS CQM or eCQM cells), or honest
 * ZEROS when that lane has no data (owner 2026-07-21: a calc row must never
 * resurrect the stale count snapshot saved in the DB row — e.g. after the
 * ACO's claims data is deleted). Manual rows pass through unchanged. Pure.
 */
export function applyCalculatedCounts(
  entries: Record<string, StoredSubmission>,
  calculated: CalculatedCountsByType
): Record<string, StoredSubmission> {
  const out: Record<string, StoredSubmission> = {};
  for (const [id, row] of Object.entries(entries)) {
    if (row.payload?.mode !== 'calc') {
      out[id] = row;
      continue;
    }
    const counts = calculated[id]?.[row.collection_type];
    out[id] = counts
      ? {
          ...row,
          reported: true,
          eligible_population: counts.ip,
          denominator_exclusions: counts.ex,
          performance_met: counts.met,
          denominator_exceptions: counts.exc,
          performance_not_met: counts.not,
        }
      : {
          // No data in this lane: zero counts (the engine excludes the measure
          // via case minimum). `reported` stays as stored so "Mark not
          // reported" still works on a dataless calc row.
          ...row,
          eligible_population: 0,
          denominator_exclusions: 0,
          performance_met: 0,
          denominator_exceptions: 0,
          performance_not_met: 0,
        };
  }
  return out;
}

// ===== Lane memory (2026-09-12) =====
//
// One submission row per measure, but the user may want e.g. Medicare CQM
// scored from pipeline data while MIPS CQM holds manually entered counts.
// `payload.lanes[collectionType]` remembers each lane's mode + manual counts;
// the top-level columns ALWAYS mirror the selected lane (`collection_type`),
// so the engine, decodeSubmissions, scores and every reader are unchanged.
// Rows saved before this feature have no `lanes` — the top-level values are
// treated as the selected lane's memory (seeded on first read; no migration).

const COUNT_KEYS = [
  'eligible_population', 'performance_met', 'performance_not_met',
  'denominator_exceptions', 'denominator_exclusions',
] as const;

/** The selected lane's memory as reflected by the row's top-level columns. */
function laneFromTopLevel(row: StoredSubmission): LaneMemory {
  const mode = row.payload?.mode === 'calc' ? 'calc' : 'manual';
  const lane: LaneMemory = { mode };
  if (mode === 'manual') {
    for (const k of COUNT_KEYS) lane[k] = row[k] ?? null;
  }
  return lane;
}

/**
 * Mirror the row's current top-level state into `payload.lanes[collection_type]`.
 * Call before persisting so the lane you were editing is remembered. Calc
 * lanes store only the mode (pipeline counts are never snapshotted here).
 */
export function recordLane(row: StoredSubmission): StoredSubmission {
  const lanes = { ...(row.payload?.lanes ?? {}) };
  lanes[row.collection_type] = laneFromTopLevel(row);
  return { ...row, payload: { ...(row.payload ?? {}), mode: row.payload?.mode ?? 'manual', lanes } };
}

/**
 * Return the row as it should look with `nextType` selected: the previous
 * lane is remembered, and the new lane's remembered mode + counts (or sensible
 * defaults) become the top-level state. Default for an untouched lane: 'calc'
 * when the pipeline has data for it, else 'manual' with empty counts.
 */
export function switchLane(
  row: StoredSubmission,
  nextType: string,
  calculated: CalculatedCountsByType,
): StoredSubmission {
  if (nextType === row.collection_type) return row;
  const remembered = recordLane(row);
  const lanes = remembered.payload?.lanes ?? {};
  const target: LaneMemory = lanes[nextType]
    ?? { mode: calculated[row.measure_id]?.[nextType] ? 'calc' : 'manual' };
  const next: StoredSubmission = {
    ...remembered,
    collection_type: nextType,
    payload: { ...(remembered.payload ?? {}), mode: target.mode ?? 'manual', lanes },
  };
  for (const k of COUNT_KEYS) {
    // Manual lanes restore their saved counts; calc lanes clear them (the
    // pipeline fills them via applyCalculatedCounts).
    next[k] = target.mode === 'manual' ? (target[k] ?? null) : null;
  }
  return next;
}

/** What each lane remembers for the pill indicators (data / manual / untouched). */
export function laneStatus(
  row: StoredSubmission,
  collectionType: string,
  calculated: CalculatedCountsByType,
): 'data' | 'manual' | 'none' {
  if (calculated[row.measure_id]?.[collectionType]) return 'data';
  const lane = collectionType === row.collection_type
    ? laneFromTopLevel(row)
    : row.payload?.lanes?.[collectionType];
  if (lane?.mode === 'manual' && COUNT_KEYS.some(k => lane[k] != null)) return 'manual';
  return 'none';
}

/**
 * Simulate scenario: deltaPct improves each clinical measure's rate by N
 * points (inverse-aware); waive* mirror the MIPS waive rules (clinical only).
 */
function applyDelta(entry: ClinicalMeasureEntry, deltaPct: number, isInverse: boolean): ClinicalMeasureEntry {
  const total = entry.performanceMet + entry.performanceNotMet;
  if (total <= 0) return entry;
  const rate = (entry.performanceMet / total) * 100;
  const target = isInverse
    ? Math.max(0, rate - deltaPct)
    : Math.min(100, rate + deltaPct);
  const met = Math.round((target / 100) * total);
  return { ...entry, performanceMet: met, performanceNotMet: total - met };
}

/** Inputs + config → full engine run for the year. Pure. */
export function runEngine(
  inputsOrRows: EngineInputs | StoredSubmission[],
  config: AcoScoringConfig,
  year: number,
  scenario?: SimulateScenario
): AppQualityScore {
  const inputs = Array.isArray(inputsOrRows) ? decodeSubmissions(inputsOrRows, year) : inputsOrRows;
  const s = svc();
  const { compositeSlots, measures: measureSet, outcomeMeasures, remainingMeasures } = s.getMeasureSet(year);
  const baseConstants = s.getConstants(year);
  const constants = scenario?.waiveDataCompleteness
    ? { ...baseConstants, dataCompletenessThreshold: 0 }
    : baseConstants;
  const adminConfig = s.getAdminClaimsConfig(year);

  const results: MeasureScoreResult[] = [];

  for (const rawEntry of inputs.clinical) {
    const baseMeasure = measureSet.find(m => m.measureId === rawEntry.measureId)!;
    const measure = scenario?.waiveCaseMinimum ? { ...baseMeasure, caseMinimum: 0 } : baseMeasure;
    const entry = scenario?.deltaPct
      ? applyDelta(rawEntry, scenario.deltaPct, baseMeasure.isInverse)
      : rawEntry;
    const cell = s.getBenchmarkCell(year, entry.measureId, entry.collectionType);
    results.push(scoreClinicalMeasureForAco(entry, measure, cell, constants, config.isSmallPractice));
  }

  results.push(scoreCahps(
    inputs.cahpsEntries, s.getCahpsSsms(year), constants,
    config.cahpsAdministered && inputs.cahpsAdministeredInData));

  for (const id of ['479', '484'] as const) {
    const e = inputs.adminEntries.find(a => a.measureId === id)
      ?? { measureId: id, mode: 'prior_year_params' as const };  // no entry → awaiting (null params) until data lands
    results.push(scoreAdminClaims(e, adminConfig));
  }

  // Groupings come from the data file, not literals (C12).
  return assembleAppQualityScore(
    results, config, constants, compositeSlots, outcomeMeasures, remainingMeasures);
}

export const logic2026: AppPlusYearLogic = {
  clinicalMeasureIds: CLINICAL_IDS,

  getMeasureSet: (year: number): AppMeasureSet => svc().getMeasureSet(year),
  getConstants: (year: number): AppProgramConstants => svc().getConstants(year),
  getAdminClaimsConfig: (year: number): AdminClaimsScoringConfig => svc().getAdminClaimsConfig(year),
  getBenchmarkCell: (year: number, measureId: string, collectionType: AppCollectionType): AppBenchmarkCell =>
    svc().getBenchmarkCell(year, measureId, collectionType),
  getCahpsSsms: (year: number): CahpsSsm[] => svc().getCahpsSsms(year),

  decodeSubmissions,
  configFromRow,
  applyCalculatedCounts,
  recordLane,
  switchLane,
  laneStatus,

  scoreCahps: (entries: CahpsSsmEntry[], ssms: CahpsSsm[], constants: AppProgramConstants, administered: boolean): CahpsScoreResult =>
    scoreCahps(entries, ssms, constants, administered),
  scoreAdminClaims: (entry: AdminClaimsEntry, config: AdminClaimsScoringConfig): MeasureScoreResult =>
    scoreAdminClaims(entry, config),

  runEngine,
  computeMipsExposure,
};
